import type { Lang } from "@/lib/i18n/config";

/** Delade konstanter för supportfunktionen. Används av både klient och server. */

type LocalizedLabel = Record<Lang, string>;

export const SUPPORT_CATEGORIES = [
  { id: "account", label: "Konto & inloggning", labels: { sv: "Konto & inloggning", en: "Account & sign-in", es: "Cuenta e inicio de sesión" } },
  { id: "billing", label: "Betalning & nivå", labels: { sv: "Betalning & nivå", en: "Billing & plan", es: "Pagos y plan" } },
  { id: "ad", label: "Annons & publicering", labels: { sv: "Annons & publicering", en: "Listing & publishing", es: "Anuncio y publicación" } },
  { id: "auction", label: "Auktion & bud", labels: { sv: "Auktion & bud", en: "Auction & bids", es: "Subasta y pujas" } },
  { id: "scan", label: "Videoskanning & värdering", labels: { sv: "Videoskanning & värdering", en: "Video scan & valuation", es: "Escaneo de vídeo y valoración" } },
  { id: "bug", label: "Teknik & fel", labels: { sv: "Teknik & fel", en: "Technical issues", es: "Problemas técnicos" } },
  { id: "other", label: "Övrigt", labels: { sv: "Övrigt", en: "Other", es: "Otros" } },
] as const;

export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number]["id"];

export function categoryLabel(id: string, lang: Lang = "sv"): string {
  return SUPPORT_CATEGORIES.find((c) => c.id === id)?.labels[lang] ?? ({ sv: "Övrigt", en: "Other", es: "Otros" } satisfies LocalizedLabel)[lang];
}

export const SUPPORT_STATUSES = [
  { id: "new", label: "Ny", labels: { sv: "Ny", en: "New", es: "Nuevo" } },
  { id: "open", label: "Öppen", labels: { sv: "Öppen", en: "Open", es: "Abierto" } },
  { id: "waiting", label: "Väntar på svar", labels: { sv: "Väntar på svar", en: "Waiting for reply", es: "Esperando respuesta" } },
  { id: "snoozed", label: "Snoozad", labels: { sv: "Snoozad", en: "Snoozed", es: "Pospuesto" } },
  { id: "solved", label: "Löst", labels: { sv: "Löst", en: "Solved", es: "Resuelto" } },
  { id: "spam", label: "Skräp", labels: { sv: "Skräp", en: "Spam", es: "Spam" } },
] as const;

export type SupportStatus = (typeof SUPPORT_STATUSES)[number]["id"];

export function statusLabel(id: string, lang: Lang = "sv"): string {
  return SUPPORT_STATUSES.find((s) => s.id === id)?.labels[lang] ?? id;
}

export const SUPPORT_PRIORITIES = [
  { id: "low", label: "Låg", labels: { sv: "Låg", en: "Low", es: "Baja" } },
  { id: "normal", label: "Normal", labels: { sv: "Normal", en: "Normal", es: "Normal" } },
  { id: "high", label: "Hög", labels: { sv: "Hög", en: "High", es: "Alta" } },
  { id: "urgent", label: "Brådskande", labels: { sv: "Brådskande", en: "Urgent", es: "Urgente" } },
] as const;

export type SupportPriority = (typeof SUPPORT_PRIORITIES)[number]["id"];

export function priorityLabel(id: string, lang: Lang = "sv"): string {
  return SUPPORT_PRIORITIES.find((p) => p.id === id)?.labels[lang] ?? id;
}

/** Färgklasser per prioritet (semantiska tokens). */
export function priorityClass(id: string): string {
  switch (id) {
    case "urgent":
      return "bg-destructive/15 text-destructive";
    case "high":
      return "bg-primary/15 text-primary";
    case "low":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-secondary text-secondary-foreground";
  }
}

/** Kort hjälpbank som visas innan man skickar in ett ärende. */
export const SUPPORT_FAQ: { q: string; a: string; tags: string[] }[] = [
  {
    q: "Jag får ingen bekräftelsekod till min e-post",
    a: "Koden kommer inom någon minut. Kolla skräpposten och att adressen är rätt stavad. Du kan begära en ny kod – den gamla slutar då gälla.",
    tags: ["kod", "mejl", "registrera", "logga in", "inloggning", "bekräfta", "konto"],
  },
  {
    q: "Hur ändrar jag min annons efter publicering?",
    a: "Gå till Mina annonser, välj annonsen och tryck Redigera. Du kan ändra text, pris, bilder och ort när du vill.",
    tags: ["ändra", "redigera", "annons", "pris", "text", "bild"],
  },
  {
    q: "Hur fungerar auktioner och bud?",
    a: "Sätt utgångspris och sluttid när du skapar annonsen. Bud under de sista fem minuterna förlänger auktionen med fem minuter. Vinnaren och säljaren får kontakt automatiskt när den avslutas.",
    tags: ["auktion", "bud", "budgivning", "vinna", "sluttid"],
  },
  {
    q: "Vad ingår i de olika kontonivåerna?",
    a: "Gratis räcker för några annonser i månaden. Plus och Pro ger fler annonser, video, videoskanning och djupvärdering. Allt står på prissidan.",
    tags: ["pris", "nivå", "plus", "pro", "betala", "abonnemang", "faktura"],
  },
  {
    q: "Hur säger jag upp eller tar bort mitt konto?",
    a: "Under Mitt konto finns en riskzon där du kan ta bort kontot. Du bekräftar med en kod och har 30 dagars ångerfrist innan allt raderas.",
    tags: ["avsluta", "ta bort", "radera", "konto", "säga upp"],
  },
  {
    q: "Varför blev min annons stoppad innan publicering?",
    a: "Vi granskar text, bilder och pris automatiskt. Vanligast är att flera helt olika saker blandas i samma annons, eller att en person syns tydligt på en bild. Justera och prova igen.",
    tags: ["stoppad", "granskning", "publicera", "nekad", "regler"],
  },
  {
    q: "Notiser och push fungerar inte",
    a: "Slå på notiser under Mitt konto. På iPhone måste {plats} först läggas till på hemskärmen innan push kan aktiveras.",
    tags: ["push", "notis", "notiser", "iphone", "telefon", "meddelande"],
  },
];

/** Enkel ordträff mot hjälpbanken. */
export function faqMatches(text: string, limit = 3) {
  const words = text
    .toLowerCase()
    .split(/[^a-zà-ÿ0-9]+/i)
    .filter((w) => w.length > 2);
  if (!words.length) return [];
  return SUPPORT_FAQ.map((item) => {
    const hay = `${item.q} ${item.tags.join(" ")}`.toLowerCase();
    const score = words.reduce((sum, w) => sum + (hay.includes(w) ? 1 : 0), 0);
    return { item, score };
  })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((r) => r.item);
}

/** "3 tim", "2 dgr" – kort åldersvisning för SLA-klockan. */
export function shortAge(iso: string | null | undefined): string {
  if (!iso) return "–";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "nu";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} tim`;
  return `${Math.floor(h / 24)} dgr`;
}

/** Standardavslutning om ingen egen hälsning är sparad i inställningarna. */
export const SUPPORT_SIGNATURE = "Vänliga hälsningar\nSellavo";

/** Fyller i platshållare i standardsvar, så inget {{...}} kan slinka ut. */
export function fillSupportPlaceholders(
  body: string,
  vars: { name?: string | null; ticketNo?: number | string | null },
): string {
  const name = (vars.name ?? "").trim() || "där";
  const no = vars.ticketNo != null ? String(vars.ticketNo) : "";
  return body
    .replace(/\{\{\s*(namn|name)\s*\}\}/gi, name)
    .replace(/\{\{\s*(nummer|arendenummer|ärendenummer|number)\s*\}\}/gi, no);
}

/** Tar bort en redan skriven signatur så den aldrig dubbleras. */
export function stripSupportSignature(body: string, signature?: string | null): string {
  let out = body
    .replace(/\n*\s*(med\s+)?v(ä|a)nliga\s+h(ä|a)lsningar[\s,!.]*\n*\s*(sellavo)?\s*$/i, "")
    .trimEnd();
  const sig = (signature ?? "").trim();
  if (sig && out.endsWith(sig)) out = out.slice(0, -sig.length).trimEnd();
  return out;
}

/** Texten som faktiskt skickas: ifyllda platshållare + den gemensamma hälsningen. */
export function composeSupportReply(
  body: string,
  vars: { name?: string | null; ticketNo?: number | string | null; signature?: string | null },
): string {
  const sig = (vars.signature ?? "").trim() || SUPPORT_SIGNATURE;
  const filled = stripSupportSignature(fillSupportPlaceholders(body, vars), sig).trim();
  return `${filled}\n\n${sig}`;
}
